import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  Boxes,
  Clock,
  Download,
  LayoutDashboard,
  Megaphone,
  Package,
  Pencil,
  Percent,
  Plus,
  RotateCcw,
  Search,
  ShoppingCart,
  Trash2,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type JSX, type ReactNode, type Ref } from "react";

import type { IntentName } from "@/design";
import { EmptyState } from "@/shared/components/EmptyState";
import { InlineEditField } from "@/shared/components/InlineEditField";
import { MarketplaceChip, type Marketplace } from "@/shared/components/MarketplaceChip";
import { RowActionMenu } from "@/shared/components/RowActionMenu";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Drawer } from "@/shared/ui/drawer";
import { Modal } from "@/shared/ui/modal";
import { Select } from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import { Spinner } from "@/shared/ui/spinner";

// ─────────────────────────── types + mock data ───────────────────────────
type Status = "healthy" | "low_stock" | "out_of_stock" | "oversold";
interface Row {
  id: string;
  sku: string;
  name: string;
  marketplace: Marketplace;
  on_hand: number;
  buffer: number;
  manual: number;
  reserved: number;
  bundle_r: number;
  mult: number;
  sellable: number;
  alloc: number;
  status: Status;
}
const THUMBS = [
  "#0f62fe",
  "#6366f1",
  "#0ea5e9",
  "#14b8a6",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#10b981",
  "#f97316",
];
const MKPS: Marketplace[] = ["shopee", "lazada", "tiktok"];
const MKP_LABEL: Record<Marketplace, string> = {
  shopee: "Shopee",
  lazada: "Lazada",
  tiktok: "TikTok",
};
const STATUS_INTENT: Record<Status, IntentName> = {
  healthy: "success",
  low_stock: "warning",
  out_of_stock: "neutral",
  oversold: "danger",
};
const STATUS_LABEL: Record<Status, string> = {
  healthy: "Healthy",
  low_stock: "Low stock",
  out_of_stock: "Out of stock",
  oversold: "Oversold",
};
const INITIAL_ROWS: Row[] = [
  {
    id: "1",
    sku: "0001271",
    name: "Premium Wireless Earbuds",
    marketplace: "shopee",
    on_hand: 842,
    buffer: 40,
    manual: 0,
    reserved: 24,
    bundle_r: 0,
    mult: 0.7,
    sellable: 840,
    alloc: 588,
    status: "healthy",
  },
  {
    id: "2",
    sku: "0001271",
    name: "Premium Wireless Earbuds",
    marketplace: "lazada",
    on_hand: 14,
    buffer: 0,
    manual: 4,
    reserved: 0,
    bundle_r: 0,
    mult: 1,
    sellable: 8,
    alloc: 8,
    status: "low_stock",
  },
  {
    id: "3",
    sku: "0009823",
    name: "Ergonomic Office Chair",
    marketplace: "tiktok",
    on_hand: 0,
    buffer: 0,
    manual: 0,
    reserved: 0,
    bundle_r: 0,
    mult: 1,
    sellable: 0,
    alloc: 0,
    status: "out_of_stock",
  },
  {
    id: "4",
    sku: "0004412",
    name: "Stainless Steel Bottle 750ml",
    marketplace: "shopee",
    on_hand: 10,
    buffer: 0,
    manual: 20,
    reserved: 0,
    bundle_r: 0,
    mult: 1,
    sellable: -10,
    alloc: -10,
    status: "oversold",
  },
  {
    id: "5",
    sku: "0007734",
    name: "Organic Cotton Tee",
    marketplace: "lazada",
    on_hand: 1280,
    buffer: 120,
    manual: 0,
    reserved: 140,
    bundle_r: 0,
    mult: 0.9,
    sellable: 1200,
    alloc: 1080,
    status: "healthy",
  },
  {
    id: "6",
    sku: "0003391",
    name: "Smart LED Bulb (4-pack)",
    marketplace: "shopee",
    on_hand: 22,
    buffer: 0,
    manual: 8,
    reserved: 0,
    bundle_r: 0,
    mult: 1,
    sellable: 8,
    alloc: 8,
    status: "low_stock",
  },
  {
    id: "7",
    sku: "0008820",
    name: "Bamboo Cutlery Set",
    marketplace: "tiktok",
    on_hand: 560,
    buffer: 0,
    manual: 0,
    reserved: 40,
    bundle_r: 0,
    mult: 0.8,
    sellable: 508,
    alloc: 406,
    status: "healthy",
  },
  {
    id: "8",
    sku: "0005567",
    name: "Yoga Mat Pro 6mm",
    marketplace: "lazada",
    on_hand: 3,
    buffer: 0,
    manual: 3,
    reserved: 0,
    bundle_r: 0,
    mult: 1,
    sellable: 0,
    alloc: 0,
    status: "out_of_stock",
  },
];
const fmt = (n: number): string => n.toLocaleString("en-US");
const sign = (n: number): string => (n < 0 ? "−" + fmt(Math.abs(n)) : fmt(n));

// ─────────────────────────── audit mock ───────────────────────────
interface AuditEvent {
  action: string;
  actor: string;
  sev: "info" | "notable" | "high";
  ago: string;
  field: string;
  old: string;
  neo: string;
}
function auditFor(r: Row): AuditEvent[] {
  const base: AuditEvent[] = [
    {
      action: "Multiplier changed",
      actor: "ariel@atlas.local",
      sev: "info",
      ago: "2h ago",
      field: "multiplier",
      old: "1.000",
      neo: r.mult.toFixed(3),
    },
    {
      action: "Lock set",
      actor: "fadel@atlas.local",
      sev: "info",
      ago: "5h ago",
      field: "lock",
      old: "0",
      neo: String(r.manual),
    },
    {
      action: "Transferred from Shopee",
      actor: "system (Order Sync)",
      sev: "notable",
      ago: "yesterday",
      field: "qty",
      old: "—",
      neo: "+50",
    },
    {
      action: "Fake set",
      actor: "ariel@atlas.local",
      sev: "info",
      ago: "2 days ago",
      field: "fake",
      old: "0",
      neo: String(r.buffer),
    },
    {
      action: "SKU created",
      actor: "fadel@atlas.local",
      sev: "notable",
      ago: "12 May",
      field: "sku",
      old: "—",
      neo: r.sku,
    },
  ];
  if (r.status === "oversold")
    base.unshift({
      action: "Oversold detected",
      actor: "system",
      sev: "high",
      ago: "just now",
      field: "sellable",
      old: "0",
      neo: String(r.sellable),
    });
  return base;
}
const SEV_CLASS: Record<AuditEvent["sev"], string> = {
  info: "bg-[var(--intent-neutral-bg)] text-[var(--intent-neutral-fg)]",
  notable: "bg-[var(--brand-50)] text-[var(--brand-700)]",
  high: "bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]",
};

// ─────────────────────────── small helpers ───────────────────────────
function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="mt-10">
      <h2 className="mb-4 text-[13px] font-bold uppercase tracking-[0.08em] text-[var(--text-subtle)]">
        {title}
      </h2>
      <div
        className="flex flex-wrap items-center gap-4 rounded-2xl border border-[var(--premium-border)] bg-white p-6"
        style={{ boxShadow: "var(--sh-sm)" }}
      >
        {children}
      </div>
    </section>
  );
}
function Toast({ msg }: { msg: string | null }): JSX.Element | null {
  if (!msg) return null;
  return (
    <div className="fixed bottom-6 right-6 z-[60] flex items-center gap-2.5 rounded-xl border border-[var(--premium-border)] bg-white px-4 py-3 text-sm font-semibold shadow-[var(--sh-pop)]">
      <span className="grid h-5 w-5 place-items-center rounded-md bg-[var(--brand-50)] text-[var(--brand-500)]">
        i
      </span>
      {msg}
    </div>
  );
}
function InfoDot(): JSX.Element {
  return (
    <svg
      className="ml-1 inline h-3.5 w-3.5 align-text-bottom text-[var(--text-subtle)]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}
function Th({
  children,
  right,
  spot,
  zoneStart,
  sticky,
}: {
  children?: ReactNode;
  right?: boolean;
  spot?: boolean;
  zoneStart?: boolean;
  sticky?: boolean;
}): JSX.Element {
  return (
    <th
      className="h-11 whitespace-nowrap px-4 align-middle text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]"
      style={{
        textAlign: right ? "right" : "left",
        background: spot ? "linear-gradient(180deg,#f3f8ff,#eef5ff)" : "var(--surface-2,#fbfcfe)",
        borderLeft: zoneStart ? "1px solid var(--brand-100,#d0e2ff)" : undefined,
        position: sticky ? "sticky" : undefined,
        right: sticky ? 0 : undefined,
        zIndex: sticky ? 6 : undefined,
      }}
    >
      {children}
    </th>
  );
}
function Td({
  children,
  right,
  spot,
  zoneStart,
  sticky,
  bg,
}: {
  children?: ReactNode;
  right?: boolean;
  spot?: boolean;
  zoneStart?: boolean;
  sticky?: boolean;
  bg: string;
}): JSX.Element {
  return (
    <td
      className="h-[52px] whitespace-nowrap px-4 align-middle text-sm text-[var(--text-primary)]"
      style={{
        textAlign: right ? "right" : "left",
        background: spot
          ? bg === "#ffffff"
            ? "linear-gradient(180deg,#f3f8ff,#eef5ff)"
            : "#e8f1ff"
          : bg,
        borderLeft: zoneStart ? "1px solid var(--brand-100,#d0e2ff)" : undefined,
        position: sticky ? "sticky" : undefined,
        right: sticky ? 0 : undefined,
        zIndex: sticky ? 5 : undefined,
        boxShadow: sticky ? "-10px 0 12px -10px rgba(16,24,40,.16)" : undefined,
      }}
    >
      {children}
    </td>
  );
}
function Check({
  checked,
  onChange,
  label,
  indeterminateRef,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  indeterminateRef?: Ref<HTMLInputElement>;
}): JSX.Element {
  return (
    <input
      ref={indeterminateRef}
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={onChange}
      className="h-[17px] w-[17px] cursor-pointer align-middle"
      style={{ accentColor: "var(--brand-500)" }}
    />
  );
}

// ─────────────────────────── demo table ───────────────────────────
interface TableProps {
  rows: Row[];
  selected: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  onToggleRow: (id: string) => void;
  onField: (id: string, key: "buffer" | "manual" | "mult", raw: string) => void;
  onEdit: (r: Row) => void;
  onAudit: (r: Row) => void;
  onTransfer: (r: Row) => void;
  onDelete: (r: Row) => void;
}
function InventoryTableDemo(p: TableProps): JSX.Element {
  const headRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headRef.current) headRef.current.indeterminate = p.someSelected && !p.allSelected;
  }, [p.someSelected, p.allSelected]);

  if (p.rows.length === 0) {
    return (
      <div
        className="rounded-[14px] border border-[var(--premium-border)] bg-white"
        style={{ boxShadow: "var(--sh-card)" }}
      >
        <EmptyState
          icon={<Search className="h-6 w-6" />}
          title="No SKUs match these filters"
          body="Try clearing your filters or creating a new SKU."
        />
      </div>
    );
  }

  return (
    <div
      className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] bg-white"
      style={{ boxShadow: "var(--sh-card)" }}
    >
      <table className="w-full border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <Th>
              <Check
                checked={p.allSelected}
                onChange={p.onToggleAll}
                label="Select all rows"
                indeterminateRef={headRef}
              />
            </Th>
            <Th>Product</Th>
            <Th>Marketplace</Th>
            <Th right>On-hand</Th>
            <Th right>Fake</Th>
            <Th right>
              Lock
              <InfoDot />
            </Th>
            <Th right>Reserved</Th>
            <Th right>Bundle reserve</Th>
            <Th right>Multiplier</Th>
            <Th right spot zoneStart>
              Sellable
            </Th>
            <Th right spot>
              Allocation
            </Th>
            <Th spot>Status</Th>
            <Th sticky />
          </tr>
        </thead>
        <tbody>
          {p.rows.map((r, i) => {
            const sel = p.selected.has(r.id);
            const bg = sel ? "#eef5ff" : "#ffffff";
            const initial = (r.name.match(/[A-Za-z]/)?.[0] ?? "S").toUpperCase();
            const thumb = THUMBS[(r.sku.charCodeAt(2) || 3) % THUMBS.length];
            return (
              <tr
                key={r.id}
                className="row-enter border-b border-[var(--premium-border)]"
                style={{ animationDelay: `${Math.min(i, 10) * 35}ms` }}
              >
                <Td bg={bg}>
                  <Check
                    checked={sel}
                    onChange={() => p.onToggleRow(r.id)}
                    label={`Select ${r.sku} ${r.marketplace}`}
                  />
                </Td>
                <Td bg={bg}>
                  <div className="flex items-center gap-3">
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] text-xs font-bold text-white"
                      style={{
                        background: `linear-gradient(140deg,${thumb},${thumb}cc)`,
                        boxShadow: "var(--sh-xs)",
                      }}
                    >
                      {initial}
                    </span>
                    <span className="min-w-0">
                      <span className="block max-w-[210px] truncate text-[13.5px] font-semibold">
                        {r.name}
                      </span>
                      <span className="sku block text-[11.5px] text-[var(--text-subtle)]">
                        {r.sku}
                      </span>
                    </span>
                  </div>
                </Td>
                <Td bg={bg}>
                  <MarketplaceChip marketplace={r.marketplace} />
                </Td>
                <Td right bg={bg}>
                  <span className="num">{fmt(r.on_hand)}</span>
                </Td>
                <Td right bg={bg}>
                  <InlineEditField
                    value={String(r.buffer)}
                    onSave={(v) => p.onField(r.id, "buffer", v)}
                  />
                </Td>
                <Td right bg={bg}>
                  <InlineEditField
                    value={String(r.manual)}
                    onSave={(v) => p.onField(r.id, "manual", v)}
                  />
                </Td>
                <Td right bg={bg}>
                  <span className="num">{fmt(r.reserved)}</span>
                </Td>
                <Td right bg={bg}>
                  <span className="num">{fmt(r.bundle_r)}</span>
                </Td>
                <Td right bg={bg}>
                  <InlineEditField
                    value={r.mult.toFixed(3)}
                    onSave={(v) => p.onField(r.id, "mult", v)}
                  />
                </Td>
                <Td right spot zoneStart bg={bg}>
                  <span
                    className={
                      r.sellable < 0 ? "num font-semibold text-[var(--intent-danger-fg)]" : "num"
                    }
                  >
                    {sign(r.sellable)}
                  </span>
                </Td>
                <Td right spot bg={bg}>
                  <span
                    className={
                      r.alloc < 0 ? "num font-semibold text-[var(--intent-danger-fg)]" : "num"
                    }
                  >
                    {sign(r.alloc)}
                  </span>
                </Td>
                <Td spot bg={bg}>
                  <Badge intent={STATUS_INTENT[r.status]} dot>
                    {STATUS_LABEL[r.status]}
                  </Badge>
                </Td>
                <Td sticky bg={bg}>
                  <RowActionMenu
                    actions={[
                      {
                        key: "edit",
                        label: "Edit product",
                        icon: <Pencil className="h-4 w-4 text-[var(--text-subtle)]" />,
                        onSelect: () => p.onEdit(r),
                      },
                      {
                        key: "transfer",
                        label: "Transfer stock",
                        icon: <Boxes className="h-4 w-4 text-[var(--text-subtle)]" />,
                        onSelect: () => p.onTransfer(r),
                      },
                      {
                        key: "audit",
                        label: "Audit log",
                        icon: <Clock className="h-4 w-4 text-[var(--text-subtle)]" />,
                        onSelect: () => p.onAudit(r),
                      },
                      {
                        key: "delete",
                        label: "Delete SKU",
                        icon: <Trash2 className="h-4 w-4 text-[var(--intent-danger-fg)]" />,
                        danger: true,
                        onSelect: () => p.onDelete(r),
                      },
                    ]}
                  />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────── audit drawer body ───────────────────────────
function AuditBody({ row }: { row: Row }): JSX.Element {
  const [filter, setFilter] = useState<"all" | "notable">("all");
  const events = auditFor(row).filter((e) => (filter === "all" ? true : e.sev !== "info"));
  return (
    <div>
      <div className="flex gap-2 border-b border-[var(--premium-border)] px-5 py-3">
        {(["all", "notable"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className="rounded-full border px-3 py-1 text-[11.5px] font-semibold transition"
            style={
              filter === f
                ? {
                    background: "var(--brand-50)",
                    borderColor: "var(--brand-500)",
                    color: "var(--brand-700)",
                  }
                : {
                    borderColor: "var(--premium-border-strong)",
                    color: "var(--text-secondary)",
                    background: "#fff",
                  }
            }
          >
            {f === "all" ? "All fields" : "Notable+"}
          </button>
        ))}
      </div>
      <ol className="px-5 py-2">
        {events.map((e, i) => (
          <li
            key={i}
            className="relative border-l-2 py-4 pl-6"
            style={{
              borderColor: i === events.length - 1 ? "transparent" : "var(--premium-border)",
            }}
          >
            <span
              className="absolute -left-[7px] top-[19px] h-3 w-3 rounded-full bg-white"
              style={{
                border: `2px solid ${e.sev === "high" ? "var(--intent-danger-fg)" : e.sev === "notable" ? "var(--brand-500)" : "var(--brand-400)"}`,
              }}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13.5px] font-bold">{e.action}</span>
              <span className="whitespace-nowrap text-[11.5px] text-[var(--text-subtle)]">
                {e.ago}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
              <span>{e.actor}</span>
              <span
                className={`rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase ${SEV_CLASS[e.sev]}`}
              >
                {e.sev}
              </span>
            </div>
            <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[var(--premium-border)] bg-[var(--canvas)] px-2.5 py-1.5 num text-[12.5px]">
              <span className="text-[var(--text-subtle)] line-through">{e.old}</span>
              <ArrowRight className="h-3.5 w-3.5 text-[var(--text-subtle)]" />
              <span className="font-semibold">{e.neo}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─────────────────────────── transfer body ───────────────────────────
function TransferBody({
  row,
  rows,
  onConfirm,
}: {
  row: Row;
  rows: Row[];
  onConfirm: (from: Marketplace, to: Marketplace, qty: number) => void;
}): JSX.Element {
  const [from, setFrom] = useState<Marketplace>(row.marketplace);
  const [to, setTo] = useState<Marketplace>(row.marketplace === "tiktok" ? "shopee" : "tiktok");
  const [qty, setQty] = useState(50);
  const sellableOf = (m: Marketplace): number =>
    rows.find((r) => r.sku === row.sku && r.marketplace === m)?.sellable ?? 0;
  const fromSell = sellableOf(from);
  const toSell = sellableOf(to);
  const sameErr = from === to;
  const oversold = qty > fromSell;
  return (
    <>
      <div className="space-y-4">
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">Route</div>
          <div className="flex items-center gap-2.5">
            <Select
              aria-label="Transfer from"
              value={from}
              onChange={(e) => setFrom(e.target.value as Marketplace)}
              className="flex-1"
            >
              {MKPS.map((m) => (
                <option key={m} value={m}>
                  {MKP_LABEL[m]}
                </option>
              ))}
            </Select>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--canvas)] text-[var(--text-subtle)]">
              <ArrowRight className="h-4 w-4" />
            </span>
            <Select
              aria-label="Transfer to"
              value={to}
              onChange={(e) => setTo(e.target.value as Marketplace)}
              className="flex-1"
            >
              {MKPS.map((m) => (
                <option key={m} value={m}>
                  {MKP_LABEL[m]}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">Quantity</div>
          <input
            aria-label="Transfer quantity"
            value={String(qty)}
            onChange={(e) => setQty(parseInt(e.target.value, 10) || 0)}
            className="num h-10 w-28 rounded-[10px] border border-[var(--premium-border-strong)] px-3 text-right text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
          />
        </div>
        <div
          className="rounded-xl border border-[var(--brand-100,#d0e2ff)] p-3.5 text-[13px]"
          style={{ background: "linear-gradient(180deg,#f3f8ff,#eef5ff)" }}
        >
          <div className="flex items-center justify-between py-0.5">
            <span>{MKP_LABEL[from]} sellable</span>
            <span className="num font-semibold">
              {fromSell} →{" "}
              <span className={fromSell - qty < 0 ? "text-[var(--intent-danger-fg)]" : ""}>
                {fromSell - qty}
              </span>
            </span>
          </div>
          <div className="flex items-center justify-between py-0.5">
            <span>{MKP_LABEL[to]} sellable</span>
            <span className="num font-semibold">
              {toSell} → <span className="text-[var(--intent-success-fg)]">{toSell + qty}</span>
            </span>
          </div>
          {sameErr && (
            <div className="mt-1.5 font-semibold text-[var(--intent-danger-fg)]">
              Source and destination must differ
            </div>
          )}
          {!sameErr && oversold && (
            <div className="mt-1.5 font-semibold text-[var(--intent-warning-fg)]">
              Source would go oversold
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        {/* confirm */}
        <button
          type="button"
          disabled={sameErr || qty <= 0}
          onClick={() => onConfirm(from, to, qty)}
          className="inline-flex h-10 items-center rounded-[10px] px-5 text-[13.5px] font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: "var(--grad-primary-btn)",
            boxShadow: "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5)",
          }}
        >
          Confirm transfer
        </button>
      </div>
    </>
  );
}

// ─────────────────────────── create SKU body ───────────────────────────
function CreateBody({
  onCreate,
  onCancel,
}: {
  onCreate: (sku: string, name: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [bundle, setBundle] = useState(false);
  const valid = sku.trim().length > 0 && name.trim().length > 0;
  return (
    <>
      <div className="space-y-4">
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">SKU code</div>
          <input
            aria-label="SKU code"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="e.g. 0009999"
            className="sku h-10 w-full rounded-[10px] border border-[var(--premium-border-strong)] px-3 text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
          />
        </div>
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
            Product name
          </div>
          <input
            aria-label="Product name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Travel Mug 400ml"
            className="h-10 w-full rounded-[10px] border border-[var(--premium-border-strong)] px-3 text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
          />
        </div>
        <label className="flex items-center gap-2.5 text-sm font-medium">
          <input
            type="checkbox"
            checked={bundle}
            onChange={(e) => setBundle(e.target.checked)}
            className="h-[17px] w-[17px]"
            style={{ accentColor: "var(--brand-500)" }}
          />
          Is bundle
        </label>
        <p className="text-[12.5px] text-[var(--text-subtle)]">
          Creates one SKU with a stock row per marketplace (Shopee, Lazada, TikTok).
        </p>
      </div>
      <div className="mt-5 flex justify-end gap-2.5">
        <button
          type="button"
          onClick={onCancel}
          className="h-10 rounded-[10px] border border-[var(--premium-border-strong)] px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!valid}
          onClick={() => onCreate(sku.trim(), name.trim())}
          className="inline-flex h-10 items-center gap-1.5 rounded-[10px] px-5 text-[13.5px] font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: "var(--grad-primary-btn)",
            boxShadow: "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5)",
          }}
        >
          <Plus className="h-[15px] w-[15px]" /> Create
        </button>
      </div>
    </>
  );
}

// ─────────────────────────── sidebar ───────────────────────────
const NAV: { icon: LucideIcon; label: string; active?: boolean; soon?: boolean }[] = [
  { icon: LayoutDashboard, label: "Dashboard", soon: true },
  { icon: ShoppingCart, label: "Orders", soon: true },
  { icon: Package, label: "Inventory", active: true },
  { icon: Boxes, label: "Inventory diff", soon: true },
  { icon: Percent, label: "Inventory ratio" },
  { icon: Megaphone, label: "Campaign", soon: true },
  { icon: Users, label: "Users", soon: true },
];

// ─────────────────────────── page ───────────────────────────
function PlaygroundPage(): JSX.Element {
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [mkp, setMkp] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [auditRow, setAuditRow] = useState<Row | null>(null);
  const [transferRow, setTransferRow] = useState<Row | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [lang, setLang] = useState<"en" | "th">("en");
  const [toast, setToast] = useState<string | null>(null);
  const fire = (msg: string): void => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!mkp || r.marketplace === mkp) &&
          (!status || r.status === status) &&
          (!q ||
            r.name.toLowerCase().includes(q.toLowerCase()) ||
            r.sku.toLowerCase().includes(q.toLowerCase())),
      ),
    [rows, mkp, status, q],
  );
  const filteredIds = filtered.map((r) => r.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));

  const toggleAll = (): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  const toggleRow = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const onField = (id: string, key: "buffer" | "manual" | "mult", raw: string): void => {
    const n = key === "mult" ? parseFloat(raw) : parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [key]: Math.max(0, n) } : r)));
    fire("Change saved");
  };
  const onDelete = (r: Row): void => {
    setRows((rs) => rs.filter((x) => x.id !== r.id));
    setSelected((prev) => {
      const n = new Set(prev);
      n.delete(r.id);
      return n;
    });
    fire(`Deleted ${r.sku} · ${MKP_LABEL[r.marketplace]}`);
  };
  const onCreate = (sku: string, name: string): void => {
    const newRows: Row[] = MKPS.map((m) => ({
      id: `new-${sku}-${m}-${Date.now()}`,
      sku,
      name,
      marketplace: m,
      on_hand: 0,
      buffer: 0,
      manual: 0,
      reserved: 0,
      bundle_r: 0,
      mult: 1,
      sellable: 0,
      alloc: 0,
      status: "out_of_stock",
    }));
    setRows((rs) => [...newRows, ...rs]);
    setCreateOpen(false);
    fire(`Created ${sku} (3 marketplaces)`);
  };
  const onTransfer = (from: Marketplace, to: Marketplace, qty: number): void => {
    if (!transferRow) return;
    setRows((rs) =>
      rs.map((r) =>
        r.sku === transferRow.sku && r.marketplace === from
          ? { ...r, sellable: r.sellable - qty }
          : r.sku === transferRow.sku && r.marketplace === to
            ? { ...r, sellable: r.sellable + qty }
            : r,
      ),
    );
    const sku = transferRow.sku;
    setTransferRow(null);
    fire(`Transferred ${qty} ${sku}: ${MKP_LABEL[from]} → ${MKP_LABEL[to]}`);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--grad-canvas)" }}>
      <header
        className="sticky top-0 z-40 flex h-[60px] items-center justify-between border-b border-[var(--premium-border)] px-6"
        style={{
          background: "rgba(255,255,255,.85)",
          backdropFilter: "saturate(180%) blur(12px)",
          boxShadow: "var(--sh-xs)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <span
            className="grid h-7 w-7 place-items-center rounded-lg text-white"
            style={{
              background: "linear-gradient(140deg,#3b82f6,var(--brand-500) 45%,var(--brand-700))",
              boxShadow: "var(--sh-xs)",
            }}
          >
            <Package className="h-4 w-4" />
          </span>
          <span className="text-[16px] font-extrabold tracking-tight">Atlas OMS</span>
          <span className="ml-2 rounded-full bg-[var(--brand-50)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--brand-700)]">
            Playground
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="inline-flex rounded-[9px] border border-[var(--premium-border-strong)] bg-white p-0.5"
            style={{ boxShadow: "var(--sh-xs)" }}
          >
            {(["en", "th"] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className="h-7 min-w-9 rounded-[7px] px-2.5 text-xs font-semibold transition"
                style={
                  lang === l
                    ? { background: "var(--brand-500)", color: "#fff" }
                    : { color: "var(--text-subtle)" }
                }
              >
                {l === "en" ? "EN" : "ไทย"}
              </button>
            ))}
          </div>
          <span
            className="grid h-[30px] w-[30px] place-items-center rounded-full text-xs font-bold text-white"
            style={{ background: "linear-gradient(135deg,#6366f1,#0f62fe)" }}
          >
            AD
          </span>
        </div>
      </header>

      <div className="flex">
        <aside
          className="w-[236px] shrink-0 border-r border-[var(--premium-border)] p-3"
          style={{ background: "var(--grad-sidebar)" }}
        >
          <div className="px-3 pb-2 pt-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-subtle)]">
            Operations
          </div>
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <div
                key={n.label}
                className="relative my-0.5 flex h-[38px] items-center gap-2.5 rounded-[9px] px-3 text-[13.5px] font-semibold"
                style={
                  n.active
                    ? {
                        background: "linear-gradient(135deg,var(--brand-50),#fff)",
                        color: "var(--brand-700)",
                        boxShadow: "var(--sh-xs)",
                      }
                    : { color: "var(--text-secondary)" }
                }
              >
                {n.active && (
                  <span className="absolute -left-3 bottom-2 top-2 w-[3px] rounded-r bg-[var(--brand-500)]" />
                )}
                <Icon
                  className="h-[17px] w-[17px]"
                  style={{ color: n.active ? "var(--brand-500)" : "var(--text-subtle)" }}
                />
                <span className="truncate">{n.label}</span>
                {n.soon && (
                  <span className="ml-auto rounded-full bg-[var(--surface-sunken)] px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-wide text-[var(--text-subtle)]">
                    Soon
                  </span>
                )}
              </div>
            );
          })}
        </aside>

        <main className="min-w-0 flex-1 p-7 pb-28">
          <div className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--text-subtle)]">
            Operations <span className="text-[var(--premium-border-strong)]">/</span>{" "}
            <span className="text-[var(--text-secondary)]">Inventory</span>
          </div>
          <h1 className="text-[27px] font-extrabold tracking-tight">Inventory</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {lang === "en"
              ? `${rows.length} SKUs across Shopee, Lazada and TikTok`
              : `${rows.length} SKU บน Shopee, Lazada และ TikTok`}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            <Select value={mkp} onChange={(e) => setMkp(e.target.value)}>
              <option value="">All marketplaces</option>
              {MKPS.map((m) => (
                <option key={m} value={m}>
                  {MKP_LABEL[m]}
                </option>
              ))}
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search product or SKU…"
                className="h-9 w-full rounded-[10px] border border-[var(--premium-border-strong)] bg-white pl-[38px] pr-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring"
                style={{ boxShadow: "var(--sh-xs)" }}
              />
            </div>
            <RowActionMenu
              label="More actions"
              actions={[
                {
                  key: "sync",
                  label: "Sync now",
                  icon: <RotateCcw className="h-4 w-4 text-[var(--text-subtle)]" />,
                  onSelect: () => fire("Sync — other module (coming soon)"),
                },
                {
                  key: "export",
                  label: "Export CSV",
                  icon: <Download className="h-4 w-4 text-[var(--text-subtle)]" />,
                  onSelect: () => fire("Export — coming soon"),
                },
                {
                  key: "import",
                  label: "Import transfers",
                  icon: <Download className="h-4 w-4 text-[var(--text-subtle)]" />,
                  onSelect: () => fire("Import — coming soon"),
                },
              ]}
            />
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-[10px] px-4 text-[13.5px] font-bold text-white transition hover:-translate-y-px"
              style={{
                background: "var(--grad-primary-btn)",
                boxShadow: "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5)",
              }}
            >
              <Plus className="h-[15px] w-[15px]" /> Create SKU
            </button>
          </div>

          <div className="mt-4">
            <InventoryTableDemo
              rows={filtered}
              selected={selected}
              allSelected={allSelected}
              someSelected={someSelected}
              onToggleAll={toggleAll}
              onToggleRow={toggleRow}
              onField={onField}
              onEdit={(r) => fire(`Edit ${r.sku} dialog would open`)}
              onAudit={setAuditRow}
              onTransfer={setTransferRow}
              onDelete={onDelete}
            />
          </div>
          <p className="mt-3 text-[13px] text-[var(--text-subtle)]">
            Showing <span className="num">{filtered.length}</span> of{" "}
            <span className="num">{rows.length}</span> · the header checkbox selects all{" "}
            <b>filtered</b> rows · scroll sideways → ••• stays pinned.
          </p>

          {/* primitives gallery */}
          <Section title="Buttons">
            <Button>Default</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </Section>
          <Section title="Status badges (dot + label)">
            <Badge intent="success" dot>
              Healthy
            </Badge>
            <Badge intent="warning" dot>
              Low stock
            </Badge>
            <Badge intent="neutral" dot>
              Out of stock
            </Badge>
            <Badge intent="danger" dot>
              Oversold
            </Badge>
          </Section>
          <Section title="Marketplace chips">
            <MarketplaceChip marketplace="shopee" />
            <MarketplaceChip marketplace="lazada" />
            <MarketplaceChip marketplace="tiktok" />
          </Section>
          <Section title="Feedback states">
            <Spinner size="sm" />
            <Spinner size="md" />
            <Spinner size="lg" />
            <div className="flex w-48 flex-col gap-2">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </Section>
        </main>
      </div>

      {/* bulk action bar */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-2xl px-4 py-2.5 pl-5 text-white shadow-[var(--sh-pop)]"
          style={{ background: "#131a29" }}
        >
          <span className="text-[13.5px] font-bold">{selected.size} selected</span>
          <span className="h-5 w-px bg-white/20" />
          <button
            type="button"
            onClick={() => fire(`Reset ${selected.size} to defaults`)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white/10 px-3 text-[13px] font-semibold transition hover:bg-white/20"
          >
            <RotateCcw className="h-[15px] w-[15px]" /> Reset defaults
          </button>
          <button
            type="button"
            onClick={() => fire("Bulk edit panel would open")}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[var(--brand-500)] px-3 text-[13px] font-semibold transition hover:bg-[#2f76ff]"
          >
            <Pencil className="h-[15px] w-[15px]" /> Bulk edit
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            aria-label="Clear selection"
            className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 transition hover:bg-white/20"
          >
            <Plus className="h-4 w-4 rotate-45" />
          </button>
        </div>
      )}

      {/* audit drawer */}
      <Drawer
        open={auditRow !== null}
        onClose={() => setAuditRow(null)}
        title="Audit log"
        subtitle={
          auditRow ? (
            <span>
              <span className="sku">{auditRow.sku}</span> · {MKP_LABEL[auditRow.marketplace]} ·{" "}
              {auditRow.name}
            </span>
          ) : undefined
        }
        actions={
          <button
            type="button"
            onClick={() => fire("Audit CSV exported")}
            aria-label="Export CSV"
            className="grid h-8 w-8 place-items-center rounded-lg text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] focus-ring"
          >
            <Download className="h-4 w-4" />
          </button>
        }
      >
        {auditRow && <AuditBody row={auditRow} />}
      </Drawer>

      {/* transfer modal */}
      <Modal
        open={transferRow !== null}
        onClose={() => setTransferRow(null)}
        title="Transfer stock"
        subtitle={
          transferRow ? (
            <span>
              <span className="sku">{transferRow.sku}</span> · {transferRow.name}
            </span>
          ) : undefined
        }
      >
        {transferRow && <TransferBody row={transferRow} rows={rows} onConfirm={onTransfer} />}
      </Modal>

      {/* create SKU modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create SKU"
        subtitle="Add a new SKU to the catalog"
      >
        <CreateBody onCreate={onCreate} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Toast msg={toast} />
    </div>
  );
}

export const Route = createFileRoute("/playground")({ component: PlaygroundPage });
